import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { studentApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  FileUploader,
  Icon,
  LinkButton,
  Select,
  Skeleton,
  useToast,
} from '@/components/ui';
import { RESUME_ACCEPT, RESUME_HINT, RESUME_MAX_SIZE } from '@/features/resume/upload';
import { UpgradeNotice } from '@/features/studentBilling/UpgradeNotice';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';

import {
  defaultResumeId,
  demandLabel,
  fitSentence,
  rankByDemand,
  suggestionKind,
  verdictOf,
} from './cvCheck';

const GRADE_TONES = { Strong: 'success', Good: 'brand', 'Needs work': 'warning', Weak: 'danger' };

/**
 * "Check my CV against this goal": pick a CV (or upload one) and see how far it
 * is from the goal the page is showing -- saved or only previewed.
 *
 * Three parts, labelled for what they are: the fit is counted from open
 * listings (same CV, same field, same answer), the quality is the CV review's
 * checklist, and the suggestions are AI-written on top -- or written from the
 * gap when AI is unavailable or the month's allowance is used. Nothing here
 * changes the profile.
 */
export function CvGoalCheck({ goal, categoryId, categoryName }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [picked, setPicked] = useState('');
  const { hash } = useLocation();

  // Linked as #cv-check (from the resume gap analysis). The card renders
  // after the page's data arrives, so the browser's own jump has already
  // missed it; scroll once it exists.
  useEffect(() => {
    if (hash === '#cv-check') {
      document.getElementById('cv-check')?.scrollIntoView({ block: 'start' });
    }
  }, [hash]);

  const resumesQuery = useQuery({
    queryKey: ['student-resumes'],
    queryFn: studentApi.listResumes,
  });
  const resumes = resumesQuery.data ?? [];
  const resumeId = defaultResumeId(resumes, picked);

  const upload = useMutation({
    // The first CV becomes the primary, as on the CV page; another one added
    // here for a goal never quietly replaces the CV sent with applications.
    mutationFn: (file) => studentApi.uploadResume(file, { makePrimary: resumes.length === 0 }),
    onSuccess: (resume) => {
      toast.success('CV uploaded. It is on your CV page too.');
      if (resume?.id) setPicked(resume.id);
      ['student-resumes', 'student-dashboard', 'student-profile'].forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      );
    },
    onError: (error) => toast.error(errorMessage(error, 'Upload failed')),
  });

  const check = useMutation({
    mutationFn: (variables) => studentApi.checkCvAgainstGoal(variables),
  });

  // A result belongs to the CV, goal and field it was asked about; change any
  // of them and it is no longer the answer on screen.
  const asked = check.variables;
  const isCurrent =
    asked &&
    asked.resumeId === resumeId &&
    asked.jobCategoryId === categoryId &&
    (asked.goal ?? '') === (goal ?? '');
  const result = isCurrent ? check.data : null;

  function run() {
    check.mutate({ resumeId, goal, jobCategoryId: categoryId });
  }

  return (
    <Card>
      <CardHeader
        title="Check my CV against this goal"
        description={
          goal
            ? `How close your CV is to “${goal}” in ${categoryName ?? 'this field'}, and what to do next.`
            : `How close your CV is to ${categoryName ?? 'this field'}, and what to do next.`
        }
        action={<Badge tone="neutral">Nothing is saved to your profile</Badge>}
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-3">
          {resumesQuery.isPending ? (
            <Skeleton className="h-16 w-full" />
          ) : resumes.length === 0 ? (
            <EmptyState
              compact
              icon={<Icon name="document" size={16} />}
              title="No CV yet"
              description="Upload one on the right to check it. It will also appear on your CV page."
            />
          ) : (
            <Select
              label="CV to check"
              hint="Your primary CV is picked for you. Upload another to compare."
              value={resumeId}
              onChange={(event) => setPicked(event.target.value)}
            >
              {resumes.map((resume) => (
                <option key={resume.id} value={resume.id}>
                  {resume.file_name}
                  {resume.is_primary ? ' (primary)' : ''}
                </option>
              ))}
            </Select>
          )}
          <Button
            onClick={run}
            isLoading={check.isPending}
            disabled={!resumeId || !categoryId || upload.isPending}
          >
            {!check.isPending && <Icon name="sparkles" size={15} />}
            {check.isPending ? 'Checking…' : result ? 'Check again' : 'Check my CV'}
          </Button>
        </div>

        <div>
          <p className="text-ink mb-1 text-[12px] font-medium">Or upload a new CV</p>
          <FileUploader
            accept={RESUME_ACCEPT}
            maxSizeBytes={RESUME_MAX_SIZE}
            isUploading={upload.isPending}
            hint={RESUME_HINT}
            onSelect={(file) => upload.mutate(file)}
          />
        </div>
      </div>

      {check.isPending && (
        <div className="border-line mt-5 border-t pt-5" aria-live="polite">
          <div className="flex items-center gap-3">
            <span className="bg-brand-50 text-brand-600 grid size-9 place-items-center rounded-xl">
              <Icon name="sparkles" size={18} className="animate-pulse" />
            </span>
            <div>
              <p className="text-ink text-sm font-semibold">Checking your CV…</p>
              <p className="text-ink-muted text-xs">
                Counting which skills {categoryName ?? 'this field'} asks for are on it, then
                writing suggestions. This can take up to 20 seconds.
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-2.5">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-4/6" />
          </div>
        </div>
      )}

      {check.isError && isCurrent && (
        <Alert tone="error" className="mt-5" title="We could not check this CV">
          {errorMessage(check.error)}
        </Alert>
      )}

      {result && !check.isPending && <CvGoalCheckResult result={result} />}
    </Card>
  );
}

/** The answer: verdict and counted fit, CV quality, then suggestions. */
export function CvGoalCheckResult({ result }) {
  const { fit, quality } = result;
  const verdict = verdictOf(fit);
  const found = rankByDemand(fit.found);
  const missing = rankByDemand(fit.missing);
  const failed = quality.checks.filter((item) => !item.passed);
  const isAi = result.source === 'ai';

  return (
    <div className="border-line mt-5 space-y-5 border-t pt-5">
      {result.limit_reached && (
        <UpgradeNotice title="AI CV reviews used up for this month">
          {result.upgrade_hint ??
            "You've used your AI CV reviews this month, so these suggestions are written from the gap. Upgrade for unlimited AI reviews."}
        </UpgradeNotice>
      )}

      {/* Fit for this goal: counted */}
      <section>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <FitScore score={fit.score} ring={verdict.ring} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={verdict.tone} dot>
                {verdict.label}
              </Badge>
              <Badge tone="neutral">
                <Icon name="check" size={12} />
                Counted from open listings
              </Badge>
            </div>
            <p className="text-ink mt-2 text-sm">{fitSentence(fit, result.category?.name)}</p>
            <p className="text-ink-muted mt-0.5 text-xs">
              The score is the share of demand your CV covers, weighted by how many listings ask for
              each skill. Strong fit from 70, partial from 40.
            </p>
          </div>
        </div>

        {(found.length > 0 || missing.length > 0) && (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <SkillColumn
              title="Missing, most asked for first"
              empty="Nothing missing — your CV names every skill these listings ask for."
              skills={missing}
              openListings={fit.open_listings}
              onCv={false}
            />
            <SkillColumn
              title="Already on your CV"
              empty="None of the skills these listings ask for were found on this CV yet."
              skills={found}
              openListings={fit.open_listings}
              onCv
            />
          </div>
        )}
      </section>

      {/* CV quality: the review's checklist */}
      <section className="border-line rounded-card border px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-ink text-sm font-semibold">CV quality</h3>
            <span className="text-ink text-sm font-semibold tabular-nums">
              {quality.score}
              <span className="text-ink-muted text-xs font-normal">/100</span>
            </span>
            <Badge tone={GRADE_TONES[quality.grade] ?? 'neutral'} dot>
              {quality.grade}
            </Badge>
          </div>
          <LinkButton to={paths.student.cvFeedback(result.resume_id)} variant="ghost" size="sm">
            Full CV feedback
            <Icon name="arrowRight" size={14} />
          </LinkButton>
        </div>
        {failed.length === 0 ? (
          <p className="text-ink-muted mt-1 text-xs">
            Passes all {quality.checks.length} basic checks.
          </p>
        ) : (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {failed.map((item) => (
              <li key={item.key}>
                <Badge tone="danger" title={item.detail}>
                  <Icon name="close" size={11} />
                  {item.label}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Suggestions: AI-written or from the gap */}
      <section>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-ink text-sm font-semibold">What to do next</h3>
          <Badge tone={isAi ? 'brand' : 'neutral'}>
            <Icon name={isAi ? 'sparkles' : 'check'} size={12} />
            {isAi ? 'AI suggestions' : 'Written from the gap'}
          </Badge>
        </div>
        {result.summary && <p className="text-ink mt-2 text-sm leading-6">{result.summary}</p>}
        <ol className="mt-3 space-y-2.5">
          {result.suggestions.map((item, index) => {
            const kind = suggestionKind(item.kind);
            return (
              <li key={index} className="border-line rounded-input flex gap-3 border p-3.5">
                <span className="bg-brand-600 grid size-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold text-white">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-ink-muted flex items-center gap-1 text-[11px] font-semibold tracking-wide uppercase">
                    <Icon name={kind.icon} size={12} />
                    {kind.label}
                  </p>
                  <p className="text-ink mt-0.5 text-sm font-medium">{item.title}</p>
                  <p className="text-ink-muted mt-0.5 text-sm">{item.detail}</p>
                  {item.example && (
                    <p className="bg-brand-50/70 text-ink ring-brand-100 rounded-input mt-2 px-3 py-2 text-sm ring-1 ring-inset">
                      <span className="text-brand-700 mr-1.5 text-xs font-semibold">Try:</span>
                      {item.example}
                    </p>
                  )}
                  {item.skills.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-1.5">
                      {item.skills.map((skill) => (
                        <li key={skill.name}>
                          <SuggestionSkill skill={skill} openListings={fit.open_listings} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        <p className="text-ink-muted mt-3 text-xs">
          {isAi
            ? 'Suggestions are written by AI from your CV’s text and the counted gap. Your email, phone number and links are removed first. Counts and “on your CV” marks are ours, not the AI’s.'
            : 'Written from the counted gap and the CV checklist. Only add what is true.'}
        </p>
      </section>
    </div>
  );
}

function FitScore({ score, ring }) {
  const value = score ?? 0;
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.max(0, Math.min(100, value)) / 100);
  return (
    <div className="relative size-20 shrink-0">
      <svg viewBox="0 0 100 100" className="size-full -rotate-90" aria-hidden="true">
        <circle cx="50" cy="50" r={radius} className="stroke-line" strokeWidth="10" fill="none" />
        {score !== null && score !== undefined && (
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            strokeWidth="10"
            strokeLinecap="round"
            stroke="currentColor"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={cn('transition-all duration-700', ring)}
          />
        )}
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <p className="text-ink text-xl leading-none font-semibold tabular-nums">
          {score === null || score === undefined ? '—' : `${score}%`}
        </p>
      </div>
    </div>
  );
}

function SkillColumn({ title, empty, skills, openListings, onCv }) {
  return (
    <div>
      <h3 className="text-ink-muted text-[11px] font-semibold tracking-wide uppercase">
        {title} <span className="tabular-nums">({skills.length})</span>
      </h3>
      {skills.length === 0 ? (
        <p className="text-ink-muted mt-2 text-xs">{empty}</p>
      ) : (
        <ul className="divide-line mt-1 divide-y">
          {skills.map((row) => (
            <li key={row.skill.id} className="flex items-center gap-2.5 py-2">
              <span
                aria-hidden="true"
                className={cn(
                  'grid size-5 shrink-0 place-items-center rounded-full',
                  onCv ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-800',
                )}
              >
                <Icon name={onCv ? 'check' : 'plus'} size={11} />
              </span>
              <div className="min-w-0">
                <p className="text-ink truncate text-sm font-medium">
                  {row.skill.name}
                  <span className="sr-only">{onCv ? ' — on your CV' : ' — missing'}</span>
                </p>
                <p className="text-ink-muted text-xs">{demandLabel(row, openListings)}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A skill named in a suggestion, with the facts we hold about it. */
function SuggestionSkill({ skill, openListings }) {
  if (skill.on_cv === true) {
    return (
      <Badge tone="success" title="Found on this CV">
        <Icon name="check" size={11} />
        {skill.name} · on your CV
      </Badge>
    );
  }
  if (skill.listings > 0) {
    return (
      <Badge tone="warning" title={demandLabel(skill, openListings)}>
        {skill.name} · asked in {skill.listings}
      </Badge>
    );
  }
  return <Badge tone="neutral">{skill.name}</Badge>;
}
